import clsx from "clsx";
import {
  type ComponentProps,
  FunctionComponent,
  useLayoutEffect,
  useRef,
} from "react";
import { graphql, useFragment } from "react-relay";

import TitleCellMedia from "@story/data-centers-hub/components/slides/TitleCellMedia";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import { useWipeChapterStage } from "@story/data-centers-hub/hooks/useWipeChapterStage";
import { wipeClipAnimAttrs } from "@story/data-centers-hub/config/wipeClipAnimAttrs";
import { asStill } from "@story/data-centers-hub/config/titleAssets";
import getBrowserInfo from "src/utils/get-browser-info";
import { resolveLockupCopy } from "@story/data-centers-hub/config/lockupCopy";
import {
  utahCopy,
  utahSlideMedia,
} from "@story/data-centers-hub/config/utahAssets";
import ShareButtons from "@voxmedia/duet-ui/components/article/ShareButtons";
import shareButtonsThevergeConfig from "@voxmedia/duet-ui/components/article/ShareButtons/config/ShareButtons.theverge.config";
import CommentCount from "@voxmedia/duet-ui/components/meta/CommentCount";
import { UtahSlideMeta_feedContentResource$key } from "src/__generated__/relay/UtahSlideMeta_feedContentResource.graphql";
import { useCskCommentCount } from "src/hooks/use-csk-comment-count";
import { useCskCommentsLinkUrl } from "src/hooks/use-csk-comments-link-url";
import { useCskCoralStoryId } from "src/hooks/use-csk-coral-story-id";
import { useShowStoryComments } from "src/hooks/use-show-story-comments";

import * as styles from "./UtahSlide.css";

interface Props {
  active: boolean;
  animate: boolean;
  /** Stay fully open while a later chapter covers us. */
  hold?: boolean;
  /** Play the exit wipe when returning toward Virginia / title. */
  depart?: boolean;
  /** Forward wipe in progress — keep media playing until it settles. */
  leaving?: boolean;
  /** Mount already open — session restore / soft remount (skip enter wipe). */
  resume?: boolean;
  /** Hide after the incoming wipe ends — blocks Android underlay flash. */
  muted?: boolean;
  /** Attach video `src` when this chapter is in the warm neighborhood. */
  mediaWarm?: boolean;
  featureHref?: string | null;
  utahMeta?: UtahSlideMeta_feedContentResource$key | null;
}

/**
 * Utah chapter. Desktop: full-stage Stratos (center Y wipe) + centered people
 * hed; right Getty column with O’Leary hed + dek (top→down); left-middle still
 * with byline/share meta (left→right); left-bottom still (bottom→up).
 * Narrow: Stratos and both heds open from the center left↔right. Getty is a
 * top-right corner (top→down) and the people/site stack is a bottom-left
 * corner (bottom→up); the main cell stays open between them. Dek/meta is a
 * Loudoun-sized plate wiping right→left.
 */
const UtahSlide: FunctionComponent<Props> = ({
  active,
  animate,
  hold = false,
  depart = false,
  leaving = false,
  resume = false,
  muted = false,
  mediaWarm = true,
  featureHref,
  utahMeta = null,
}) => {
  const narrow = useHubNarrowViewport();
  const rootRef = useRef<HTMLDivElement>(null);
  const plateRef = useRef<HTMLDivElement>(null);

  // Corner cells are each a third of the stage above the content-sized plate.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const plate = plateRef.current;
    if (!root || !plate) {
      return;
    }
    const apply = () => {
      root.style.setProperty("--utah-plate-h", `${plate.offsetHeight}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(plate);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--utah-plate-h");
    };
  }, [narrow]);

  const data = useFragment(
    graphql`
      fragment UtahSlideMeta_feedContentResource on FeedContentResourceInterface {
        authors {
          name
        }
        additionalBylines
        dek {
          plaintext
        }
        title
        id
        globalId
        chorusId
        chorusUuid
        resourceType
        permalink
        comments {
          enabled
          mode
        }
      }
    `,
    utahMeta,
  );

  const {
    open,
    settled,
    fillPin,
    fillLock,
    departGone,
    painted,
    mediaActive,
    mediaPlayback,
    stillsActive,
  } = useWipeChapterStage({
    active,
    animate,
    hold,
    depart,
    leaving,
    resume,
    mediaWarm,
    narrow,
  });

  const lockup = resolveLockupCopy(data, utahCopy);
  const permalink = data?.permalink ?? featureHref ?? "";
  const title = data?.title ?? utahCopy.shareTitle;
  const coralId = useCskCoralStoryId({
    chorusId: data?.chorusId,
    chorusUuid: data?.chorusUuid,
    globalId: data?.globalId,
    nodeId: data?.id,
  });
  const showComments = useShowStoryComments(data?.comments ?? null);
  const utahChrome = active;
  const showMeta = Boolean(permalink);
  useCskCommentCount(showComments && data ? coralId : undefined);
  const commentsLinkUrl = useCskCommentsLinkUrl(permalink);

  const articleHref = featureHref ?? (permalink || null);

  const clipAnim = wipeClipAnimAttrs({
    animate,
    resume,
    active,
    hold,
    depart,
    open,
    fillPin,
    fillLock,
    departGone,
  });
  const clip = (fromClass: string) =>
    getBrowserInfo()?.android
      ? clsx(fromClass, open && styles.open)
      : clsx(open ? styles.open : fromClass);

  const panel = (
    <div
      ref={rootRef}
      className={clsx(
        styles.root,
        painted && !muted && styles.rootActive,
        muted && styles.rootMuted,
        settled && styles.rootSettled,
        !animate && styles.ruled,
      )}
      data-utah-open={utahChrome ? "" : undefined}
      aria-hidden={active && settled ? undefined : true}
      {...clipAnim}
    >
      {articleHref ? (
        <a className={styles.slideLink} href={articleHref}>
          <span className="sr-only">{utahCopy.hedAlt}</span>
        </a>
      ) : (
        <h2 className="sr-only">{utahCopy.hedAlt}</h2>
      )}

      <div
        className={clsx(
          styles.stage,
          clip(narrow ? styles.fromCenterX : styles.fromCenterY),
        )}
      >
        <div className={styles.mediaFill}>
          <TitleCellMedia
            // Narrow: poster only — chapter loops + title mosaic crashed iOS.
            media={
              narrow
                ? asStill(utahSlideMedia.stratos)
                : utahSlideMedia.stratos
            }
            active={mediaActive}
            // Keep src on hold underlays; play only while on-stage / wiping.
            playback={mediaPlayback}
          />
        </div>
      </div>

      {/* Heds ride in their own clipped layer, not the video's cell — see CSS. */}
      <div
        className={clsx(
          styles.stageHeds,
          clip(narrow ? styles.fromCenterX : styles.fromCenterY),
        )}
        aria-hidden="true"
      >
        <div className={styles.hedPeople}>
          <img
            className={styles.hedPeopleImg}
            src={utahSlideMedia.hedPeople}
            alt=""
            aria-hidden="true"
            decoding="async"
            loading="eager"
            fetchPriority={utahChrome ? "high" : "low"}
          />
        </div>
        {narrow ? (
          <div className={styles.hedOlearySlot}>
            <img
              className={styles.hedOlearyImg}
              src={utahSlideMedia.hedOleary}
              alt=""
              aria-hidden="true"
              decoding="async"
              loading="eager"
              fetchPriority={utahChrome ? "high" : "low"}
            />
          </div>
        ) : null}
      </div>

      {narrow ? (
        <div
          className={clsx(styles.stageLead, open && styles.stageLeadOpen)}
          aria-hidden="true"
        />
      ) : null}

      {animate && !narrow ? (
        <div
          className={clsx(styles.frame, open && styles.frameOpen)}
          aria-hidden="true"
        />
      ) : null}

      <div
        className={clsx(
          styles.rightShell,
          styles.leadFromTop,
          open && styles.leadFromTopOpen,
        )}
      >
        <div
          className={clsx(styles.right, clip(styles.fromTop))}
        >
          <div className={styles.mediaFill}>
            <TitleCellMedia
              // Narrow: all chapter loops → posters (see stratos above).
              media={
                narrow ? asStill(utahSlideMedia.getty) : utahSlideMedia.getty
              }
              active={mediaActive}
              playback={mediaPlayback}
            />
          </div>
          {narrow ? null : (
            <div className={styles.rightLockup}>
              <div className={styles.hedOleary}>
                <img
                  className={styles.hedOlearyImg}
                  src={utahSlideMedia.hedOleary}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  loading="eager"
                  fetchPriority={utahChrome ? "high" : "low"}
                />
              </div>
              <p className={styles.dek}>{lockup.lede}</p>
            </div>
          )}
        </div>
      </div>

      {narrow ? (
        <div
          className={clsx(
            styles.stackShell,
            styles.leadFromBottom,
            open && styles.leadFromBottomOpen,
          )}
        >
          <div
            className={clsx(
              styles.stack,
              clip(styles.fromBottom),
            )}
          >
            <div className={styles.stackPane}>
              <img
                className={clsx(styles.stackPhoto, styles.stackPhotoPeople)}
                src={utahSlideMedia.people.src}
                alt=""
                aria-hidden="true"
                decoding="async"
                loading="eager"
                fetchPriority={stillsActive ? "high" : "low"}
              />
            </div>
            <div className={styles.stackPane}>
              <img
                className={styles.stackPhoto}
                src={utahSlideMedia.site.src}
                alt=""
                aria-hidden="true"
                decoding="async"
                loading="eager"
                fetchPriority={stillsActive ? "high" : "low"}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Left meta — content-sized plate above the people still; left → right. */}
      {narrow ? null : (
      <>
      <div
        className={clsx(
          styles.metaShell,
          styles.leadFromLeft,
          open && styles.leadFromLeftOpen,
        )}
      >
        <div
          className={clsx(
            styles.metaCell,
            clip(styles.fromLeft),
          )}
        >
          <div className={styles.bylinePlate}>
            <div className={styles.bylines}>
              <p className={styles.byline}>
                {lockup.bylineLabel}{" "}
                <strong className={styles.bylineName}>{lockup.bylineName}</strong>
              </p>
              <p className={styles.byline}>
                {lockup.photoBylineLabel}{" "}
                <strong className={styles.bylineName}>
                  {lockup.photoBylineName}
                </strong>
              </p>
            </div>
            {showMeta ? (
              <div className={styles.meta}>
                <ShareButtons
                  {...(shareButtonsThevergeConfig as ComponentProps<
                    typeof ShareButtons
                  >)}
                  url={permalink}
                  title={title}
                  layout="standard"
                />
                {showComments && data && coralId ? (
                  <CommentCount
                    coralId={coralId}
                    url={commentsLinkUrl}
                    authorName={data.authors?.[0]?.name ?? ""}
                    contentType={data.resourceType ?? ""}
                    headline={title}
                    variant="lede"
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Left people still — same height as site; left → right. */}
      <div
        className={clsx(
          styles.midShell,
          styles.leadFromLeft,
          open && styles.leadFromLeftOpen,
        )}
      >
        <div
          className={clsx(styles.mid, clip(styles.fromLeft))}
        >
          <img
            className={styles.midPhoto}
            src={utahSlideMedia.people.src}
            alt=""
            aria-hidden="true"
            decoding="async"
            loading="eager"
            fetchPriority={stillsActive ? "high" : "low"}
          />
        </div>
      </div>

      {/* Left bottom — site still; bottom → up. */}
      <div
        className={clsx(
          styles.bottomShell,
          styles.leadFromBottom,
          open && styles.leadFromBottomOpen,
        )}
      >
        <div
          className={clsx(
            styles.bottom,
            clip(styles.fromBottom),
          )}
        >
          <TitleCellMedia media={utahSlideMedia.site} active={stillsActive} />
        </div>
      </div>
      </>
      )}

      {narrow ? (
        <div
          ref={plateRef}
          className={clsx(
            styles.plateShell,
            styles.leadFromRight,
            open && styles.leadFromRightOpen,
          )}
        >
          <div
            className={clsx(
              styles.plateCell,
              clip(styles.fromRight),
            )}
          >
            <div className={styles.bylinePlate}>
              <p className={styles.dek}>{lockup.lede}</p>
              <div className={styles.bylines}>
                <p className={styles.byline}>
                  {lockup.bylineLabel}{" "}
                  <strong className={styles.bylineName}>
                    {lockup.bylineName}
                  </strong>
                </p>
                <p className={styles.byline}>
                  {lockup.photoBylineLabel}{" "}
                  <strong className={styles.bylineName}>
                    {lockup.photoBylineName}
                  </strong>
                </p>
              </div>
              {showMeta ? (
                <div className={styles.meta}>
                  <ShareButtons
                    {...(shareButtonsThevergeConfig as ComponentProps<
                      typeof ShareButtons
                    >)}
                    url={permalink}
                    title={title}
                    layout="standard"
                  />
                  {showComments && data && coralId ? (
                    <CommentCount
                      coralId={coralId}
                      url={commentsLinkUrl}
                      authorName={data.authors?.[0]?.name ?? ""}
                      contentType={data.resourceType ?? ""}
                      headline={title}
                      variant="lede"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return panel;
};

export default UtahSlide;
